import React from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  BarChart3Icon,
  FileTextIcon,
  MessageSquareIcon,
  ClockIcon,
  CheckCircleIcon,
  TrendingUpIcon,
} from "lucide-react";
import { Agent } from "../../model_gen/agent";

interface AgentStatistics {
  intent_count: number;
  conversation_count: number;
  total_requests: number;
  avg_response_time: number;
  success_rate: number;
}

interface AgentStatsModalProps {
  open: boolean;
  onClose: () => void;
  agent: Agent;
  statistics?: AgentStatistics;
}

const AgentStatsModal: React.FC<AgentStatsModalProps> = ({
  open,
  onClose,
  agent,
  statistics,
}) => {
  const formatResponseTime = (ms: number) => {
    if (ms < 1000) return `${ms.toFixed(0)}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatSuccessRate = (rate: number) => {
    return `${(rate * 100).toFixed(1)}%`;
  };

  const statsCards = [
    {
      title: "Intents",
      value: statistics?.intent_count || 0,
      icon: FileTextIcon,
      description: "Total intents assigned",
      color: "text-blue-600",
      bgColor: "bg-blue-50",
    },
    {
      title: "Conversations",
      value: statistics?.conversation_count || 0,
      icon: MessageSquareIcon,
      description: "Active conversations",
      color: "text-green-600",
      bgColor: "bg-green-50",
    },
    {
      title: "Total Requests",
      value: statistics?.total_requests || 0,
      icon: TrendingUpIcon,
      description: "Requests processed",
      color: "text-purple-600",
      bgColor: "bg-purple-50",
    },
    {
      title: "Avg Response Time",
      value: formatResponseTime(statistics?.avg_response_time || 0),
      icon: ClockIcon,
      description: "Average response time",
      color: "text-orange-600",
      bgColor: "bg-orange-50",
    },
    {
      title: "Success Rate",
      value: formatSuccessRate(statistics?.success_rate || 0),
      icon: CheckCircleIcon,
      description: "Request success rate",
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3Icon className="w-5 h-5" />
            Agent Statistics - {agent.agent_name}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {agent.description && (
            <div className="p-4 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground">{agent.description}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {statsCards.map((stat) => {
              const Icon = stat.icon;
              return (
                <Card key={stat.title} className="border-border">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                        <Icon className={`w-4 h-4 ${stat.color}`} />
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground font-gc-reg">
                          {stat.title}
                        </p>
                        <p className="text-2xl font-gc-demi-bold text-foreground">
                          {stat.value}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {stat.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {!statistics && (
            <div className="text-center py-8">
              <BarChart3Icon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No statistics available</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AgentStatsModal; 